import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { PartySelectorComponent } from '../../shared/design-system/party-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthCommsService } from './growth-comms.service';
import {
  ConsentMatrixCellForm,
  FrequencyCapForm,
  PreferredChannelForm,
  QuietHoursForm,
  QUIET_HOUR_DAYS,
  buildConsentMatrix,
  buildCommsChannelOptions,
  buildCommsPurposeOptions,
  buildConsentStateOptions,
  createDefaultQuietHoursForm,
  createFrequencyCapRow,
  createPreferredChannelRow,
  normalizeCommsChannelKey,
  normalizeCommsPurposeKey,
  parseFrequencyCaps,
  parsePreferredChannels,
  parseQuietHoursForm
} from './growth-comms.models';

@Component({
  standalone: true,
  selector: 'ain-growth-consent-preference-center',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule,
    CheckboxModule, TabsModule, PartySelectorComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.consent.preferenceCenter.title')"
      [description]="t('growth.consent.preferenceCenter.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.consent.preferenceCenter.help.page.title'"
        [descriptionKey]="'growth.consent.preferenceCenter.help.page.description'"
        [bulletKeys]="[
          'growth.consent.preferenceCenter.help.page.bullets.action',
          'growth.consent.preferenceCenter.help.page.bullets.workflow',
          'growth.consent.preferenceCenter.help.page.bullets.warning',
          'growth.consent.preferenceCenter.help.page.bullets.afterSave'
        ]" />

      <section class="card">
        <div class="section-title">
          <h3>{{ t('growth.consent.preferenceCenter.sections.party') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.consent.preferenceCenter.help.party.title'"
            [descriptionKey]="'growth.consent.preferenceCenter.help.party.description'"
            [bulletKeys]="['growth.consent.preferenceCenter.help.party.bullets.how']" />
        </div>
        <div class="toolbar lookup-toolbar">
          <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.party')">
            <ain-party-selector [(ngModel)]="partyId" />
          </ain-form-field>
          <div class="lookup-actions">
            <button pButton type="button" [label]="t('catalog.action.search')" (click)="load()"></button>
          </div>
        </div>
      </section>

      @if (loading()) {
        <ain-state type="loading" [title]="t('growth.consent.preferenceCenter.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('growth.consent.preferenceCenter.errorTitle')" [message]="error() ?? ''" />
      } @else if (loaded()) {
        <p-tabs value="consent">
          <p-tablist>
            <p-tab value="consent">{{ t('growth.consent.preferenceCenter.tabs.consent') }}</p-tab>
            <p-tab value="preferences">{{ t('growth.consent.preferenceCenter.tabs.preferences') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="consent">
              <section class="card">
                <div class="section-title">
                  <h3>{{ t('growth.consent.preferenceCenter.sections.consentMatrix') }}</h3>
                  <ain-info-hint
                    [titleKey]="'growth.consent.preferenceCenter.help.consent.title'"
                    [descriptionKey]="'growth.consent.preferenceCenter.help.consent.description'"
                    [bulletKeys]="[
                      'growth.consent.preferenceCenter.help.consent.bullets.marketing',
                      'growth.consent.preferenceCenter.help.consent.bullets.transactional',
                      'growth.consent.preferenceCenter.help.consent.bullets.legalBasis',
                      'growth.consent.preferenceCenter.help.consent.bullets.afterSave'
                    ]" />
                </div>
                <p-table [value]="consentMatrix()" [scrollable]="true">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ t('growth.consent.preferenceCenter.columns.purpose') }}</th>
                      <th>{{ t('growth.consent.preferenceCenter.columns.channel') }}</th>
                      <th>{{ t('growth.consent.preferenceCenter.columns.state') }}</th>
                      <th>{{ t('growth.consent.preferenceCenter.columns.legalBasis') }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr>
                      <td>{{ t('growth.comms.purpose.' + row.purposeKey) }}</td>
                      <td>{{ t('growth.comms.channel.' + row.channelKey) }}</td>
                      <td>
                        <p-select
                          [options]="consentStateOptions()"
                          optionLabel="label"
                          optionValue="value"
                          [(ngModel)]="row.state"
                          appendTo="body" />
                      </td>
                      <td>
                        <input pInputText [(ngModel)]="row.legalBasis" />
                      </td>
                    </tr>
                  </ng-template>
                </p-table>
                <div class="toolbar">
                  <button pButton type="button" [label]="t('common.save')" (click)="saveConsent()" [loading]="saving()"></button>
                </div>
              </section>
            </p-tabpanel>
            <p-tabpanel value="preferences">
              <section class="card">
                <div class="section-title">
                  <h3>{{ t('growth.consent.preferenceCenter.sections.quietHours') }}</h3>
                  <ain-info-hint
                    [titleKey]="'growth.consent.preferenceCenter.help.quietHours.title'"
                    [descriptionKey]="'growth.consent.preferenceCenter.help.quietHours.description'"
                    [bulletKeys]="[
                      'growth.consent.preferenceCenter.help.quietHours.bullets.how',
                      'growth.consent.preferenceCenter.help.quietHours.bullets.effect',
                      'growth.consent.preferenceCenter.help.quietHours.bullets.afterSave'
                    ]" />
                </div>
                <div class="filter-grid">
                  <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.enabled')">
                    <p-checkbox [(ngModel)]="quietHours().enabled" [binary]="true" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.timezone')">
                    <input pInputText [(ngModel)]="quietHours().timezone" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.startTime')">
                    <input pInputText [(ngModel)]="quietHours().startTime" />
                  </ain-form-field>
                  <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.endTime')">
                    <input pInputText [(ngModel)]="quietHours().endTime" />
                  </ain-form-field>
                </div>
              </section>

              <section class="card">
                <div class="section-title">
                  <h3>{{ t('growth.consent.preferenceCenter.sections.preferredChannels') }}</h3>
                  <ain-info-hint
                    [titleKey]="'growth.consent.preferenceCenter.help.preferredChannels.title'"
                    [descriptionKey]="'growth.consent.preferenceCenter.help.preferredChannels.description'"
                    [bulletKeys]="[
                      'growth.consent.preferenceCenter.help.preferredChannels.bullets.how',
                      'growth.consent.preferenceCenter.help.preferredChannels.bullets.consent'
                    ]" />
                </div>
                @for (row of preferredChannels(); track row.localId) {
                  <div class="filter-grid row-editor">
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.columns.channel')">
                      <p-select
                        [options]="channelOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [(ngModel)]="row.channelKey"
                        appendTo="body" />
                    </ain-form-field>
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.priority')">
                      <p-inputNumber [(ngModel)]="row.priority" [min]="1" />
                    </ain-form-field>
                    <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removePreferredChannel(row.localId)"></button>
                  </div>
                }
                <button pButton type="button" severity="secondary" [label]="t('growth.consent.preferenceCenter.actions.addChannel')" (click)="addPreferredChannel()"></button>
              </section>

              <section class="card">
                <div class="section-title">
                  <h3>{{ t('growth.consent.preferenceCenter.sections.frequencyCaps') }}</h3>
                  <ain-info-hint
                    [titleKey]="'growth.consent.preferenceCenter.help.frequencyCaps.title'"
                    [descriptionKey]="'growth.consent.preferenceCenter.help.frequencyCaps.description'"
                    [bulletKeys]="[
                      'growth.consent.preferenceCenter.help.frequencyCaps.bullets.how',
                      'growth.consent.preferenceCenter.help.frequencyCaps.bullets.warning'
                    ]" />
                </div>
                @for (row of frequencyCaps(); track row.localId) {
                  <div class="filter-grid row-editor">
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.columns.purpose')">
                      <p-select [options]="purposeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.purposeKey" appendTo="body" />
                    </ain-form-field>
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.columns.channel')">
                      <p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="row.channelKey" appendTo="body" />
                    </ain-form-field>
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.maxPerDay')">
                      <p-inputNumber [(ngModel)]="row.maxPerDay" [min]="0" />
                    </ain-form-field>
                    <ain-form-field [label]="t('growth.consent.preferenceCenter.fields.maxPerWeek')">
                      <p-inputNumber [(ngModel)]="row.maxPerWeek" [min]="0" />
                    </ain-form-field>
                    <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeFrequencyCap(row.localId)"></button>
                  </div>
                }
                <button pButton type="button" severity="secondary" [label]="t('growth.consent.preferenceCenter.actions.addCap')" (click)="addFrequencyCap()"></button>
              </section>

              <div class="toolbar">
                <button pButton type="button" [label]="t('common.save')" (click)="savePreferences()" [loading]="saving()"></button>
              </div>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </ain-page>
  `,
  styles: [`
    .lookup-toolbar { display: flex; gap: 1rem; align-items: flex-end; flex-wrap: wrap; }
    .lookup-actions { display: flex; gap: .5rem; }
    .filter-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; }
    .row-editor { margin-bottom: 1rem; align-items: end; }
    .toolbar { display: flex; justify-content: flex-end; margin-top: 1rem; }
    .section-title { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
    .card { background: var(--surface-card); border-radius: 8px; padding: 1rem; margin-bottom: 1rem; }
  `]
})
export class GrowthConsentPreferenceCenterComponent {
  private readonly comms = inject(GrowthCommsService);
  private readonly i18n = inject(LocalizationService);

  partyId: string | null = null;
  loading = signal(false);
  saving = signal(false);
  loaded = signal(false);
  error = signal<string | null>(null);
  consentMatrix = signal<ConsentMatrixCellForm[]>([]);
  quietHours = signal<QuietHoursForm>(createDefaultQuietHoursForm());
  preferredChannels = signal<PreferredChannelForm[]>([]);
  frequencyCaps = signal<FrequencyCapForm[]>([]);
  locale = 'en-US';
  isEnabled = true;

  consentStateOptions = () => buildConsentStateOptions(key => this.t(key));
  channelOptions = () => buildCommsChannelOptions(key => this.t(key));
  purposeOptions = () => buildCommsPurposeOptions(key => this.t(key));

  t(key: string): string {
    return this.i18n.translate(key);
  }

  load(): void {
    if (!this.partyId) return;
    this.loading.set(true);
    this.error.set(null);
    this.comms.getConsent(this.partyId).subscribe({
      next: consent => {
        this.consentMatrix.set(buildConsentMatrix(consent.map(item => ({
          purposeKey: item.purposeKey,
          channelKey: item.channelKey,
          state: item.state,
          legalBasis: item.legalBasis,
          evidence: item.evidence
        }))));
        this.comms.getPreferenceCenter(this.partyId!).subscribe({
          next: profile => {
            this.locale = profile.locale;
            this.isEnabled = profile.isEnabled;
            this.quietHours.set(profile.quietHours ?? parseQuietHoursForm('{}'));
            this.preferredChannels.set((profile.preferredChannels ?? []).length
              ? profile.preferredChannels.map(item => ({
                  localId: crypto.randomUUID(),
                  channelKey: normalizeCommsChannelKey(item.channelKey),
                  priority: item.priority ?? 1
                }))
              : []);
            this.frequencyCaps.set((profile.frequencyCaps ?? []).length
              ? profile.frequencyCaps.map(item => ({
                  localId: crypto.randomUUID(),
                  purposeKey: normalizeCommsPurposeKey(item.purposeKey),
                  channelKey: normalizeCommsChannelKey(item.channelKey),
                  maxPerDay: item.maxPerDay ?? 1,
                  maxPerWeek: item.maxPerWeek ?? 5
                }))
              : []);
            this.loaded.set(true);
            this.loading.set(false);
          },
          error: () => {
            this.error.set(this.t('growth.consent.preferenceCenter.errorLoad'));
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.error.set(this.t('growth.consent.preferenceCenter.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  saveConsent(): void {
    if (!this.partyId) return;
    this.saving.set(true);
    this.comms.updateConsent(this.partyId, this.consentMatrix().map(cell => ({
      purposeKey: cell.purposeKey,
      channelKey: cell.channelKey,
      state: cell.state,
      legalBasis: cell.legalBasis || null,
      evidence: cell.evidence,
      source: 'admin'
    }))).subscribe({
      next: () => this.saving.set(false),
      error: () => {
        this.error.set(this.t('growth.consent.preferenceCenter.errorSave'));
        this.saving.set(false);
      }
    });
  }

  savePreferences(): void {
    if (!this.partyId) return;
    this.saving.set(true);
    this.comms.updatePreferenceCenter(this.partyId, {
      locale: this.locale,
      preferredChannels: this.preferredChannels(),
      quietHours: this.quietHours(),
      frequencyCaps: this.frequencyCaps(),
      isEnabled: this.isEnabled
    }).subscribe({
      next: () => this.saving.set(false),
      error: () => {
        this.error.set(this.t('growth.consent.preferenceCenter.errorSave'));
        this.saving.set(false);
      }
    });
  }

  addPreferredChannel(): void {
    this.preferredChannels.update(rows => [...rows, createPreferredChannelRow()]);
  }

  removePreferredChannel(localId: string): void {
    this.preferredChannels.update(rows => rows.filter(item => item.localId !== localId));
  }

  addFrequencyCap(): void {
    this.frequencyCaps.update(rows => [...rows, createFrequencyCapRow()]);
  }

  removeFrequencyCap(localId: string): void {
    this.frequencyCaps.update(rows => rows.filter(item => item.localId !== localId));
  }
}
